import { useState, useEffect, useRef, useCallback } from "react";

type Props = {
  backgroundColor: string;
  caseSize: number;
  enableBlinkingAndResizing: boolean;
  isPausedAndNotFullScreenRef: React.MutableRefObject<boolean>;
  targetPositions: {
    targets: { position: string; size: number; gif: string }[];
  }[];
};
type Target = {
  gif: string;
  position: string;
  size: number;
};

type Salvo = {
  targets: Target[];
};

const ChampsProfondIdentification = ({
  enableBlinkingAndResizing = false, // New parameter to enable blinking and resizing
  targetPositions,
  caseSize,
  isPausedAndNotFullScreenRef,
  backgroundColor,
}: Props) => {
  const [currentTargets, setCurrentTargets] = useState<
    { coordinates: { x: number; y: number }[]; gif: string; size: number }[]
  >([]);
  const [blinkAndResizeTargets, setBlinkAndResizeTargets] = useState<number[]>(
    []
  );
  // console.log('document.fullscreenElement', !!document.fullscreenElement);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timeoutId = useRef<number | undefined>(undefined);
  const [canShowTarget, setCanShowTarget] = useState(true);
  const [quadrillageVisible, setQuadrillageVisible] = useState(false);
  const [animationState, setAnimationState] = useState({
    scale: 1,
    opacity: 1,
  });
  const cellWidth = (5.5 / 4) * caseSize;
  const cellHeight = caseSize;

  const convertGridPositionToCoordinates = (
    position: string,
    columnsToRemove: number,
    rowsToRemove: number,
    cellWidth: number,
    cellHeight: number
  ) => {
    const letter = position.charAt(0).toUpperCase();
    const number = parseInt(position.slice(1), 10);
    const col = letter.charCodeAt(0) - 65;
    const row = number - 1;
    const x = col * cellWidth;
    const y = row * cellHeight;

    // Adjust position for removed columns and rows
    const adjustedX = x - columnsToRemove * cellWidth;
    const adjustedY = y - rowsToRemove * cellHeight;

    // // Check if adjusted coordinates are valid
    // if (adjustedX < 0 || adjustedY < 0) {
    //   console.warn(
    //     `Cible en dehors des limites après ajustement : (${adjustedX}, ${adjustedY})`
    //   );
    // }

    return { x: adjustedX, y: adjustedY };
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === " ") {
        if (currentTargets.length > 0 && !isPausedAndNotFullScreenRef.current) {
          clearTimeout(timeoutId.current);
          setCurrentTargets([]);
          showNewTarget();
        }
      }
      // else if (e.key === "s") {
      //   showNewTarget();
      // }
      if (e.key === "q" || e.key === "Q") {
        setQuadrillageVisible((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      clearTimeout(timeoutId.current);
    };
  }, [currentTargets]);

  const calculateCanvasDimensions = useCallback(() => {
    const maxCanvasWidth = window.innerWidth;
    const maxCanvasHeight = window.innerHeight;

    let maxColumns = 16;
    let maxRows = 12;

    let canvasWidth = maxColumns * cellWidth;
    let canvasHeight = maxRows * cellHeight;

    let columnsToRemove = 0;
    if (canvasWidth > maxCanvasWidth) {
      const excessColumns = Math.ceil(
        (canvasWidth - maxCanvasWidth) / cellWidth
      );
      columnsToRemove = Math.ceil(excessColumns / 2);
      maxColumns -= 2 * columnsToRemove;
      canvasWidth = maxColumns * cellWidth;
    }

    let rowsToRemove = 0;
    if (canvasHeight > maxCanvasHeight) {
      const excessRows = Math.ceil(
        (canvasHeight - maxCanvasHeight) / cellHeight
      );
      rowsToRemove = Math.ceil(excessRows / 2);
      maxRows -= 2 * rowsToRemove;
      canvasHeight = maxRows * cellHeight;
    }
    //  console.log("Champrofond indentification", "canvasWidth", canvasWidth, "canvasHeight", canvasHeight, "maxColumns", maxColumns, "maxRows", maxRows, "columnsToRemove", columnsToRemove, "rowsToRemove", rowsToRemove);
    //  console.log("columns", Math.floor((maxCanvasWidth/caseSize) / 2), "rows", Math.floor((maxCanvasHeight/caseSize) / 2));
    return {
      canvasWidth,
      canvasHeight,
      maxColumns,
      maxRows,
      columnsToRemove,
      rowsToRemove,
    };
  }, [cellWidth, cellHeight]);

  useEffect(() => {
    const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvasWidth, canvasHeight);

    if (quadrillageVisible) {
      // Couleur des lignes du quadrillage
      if (backgroundColor === "black") {
        context.strokeStyle = "white";
      } else {
        context.strokeStyle = "black";
      }
      context.lineWidth = 1;

      // Dessiner les lignes verticales
      for (let x = 0; x <= canvasWidth; x += cellWidth) {
        context.beginPath();
        context.moveTo(x, 0); // Commencer en haut
        context.lineTo(x, canvasHeight); // Descendre jusqu'en bas
        context.stroke();
      }

      // Dessiner les lignes horizontales
      for (let y = 0; y <= canvasHeight; y += cellHeight) {
        context.beginPath();
        context.moveTo(0, y); // Commencer à gauche
        context.lineTo(canvasWidth, y); // Aller jusqu'à droite
        context.stroke();
      }
    }

    // Draw the cross in the center
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const crossLength = (cellHeight / 4) * 0.7;

    context.beginPath();
    context.strokeStyle = backgroundColor === "white" ? "black" : "white";
    context.lineWidth = 2;
    context.moveTo(centerX - crossLength / 2, centerY);
    context.lineTo(centerX + crossLength / 2, centerY);
    context.stroke();

    context.beginPath();
    context.moveTo(centerX, centerY - crossLength / 2);
    context.lineTo(centerX, centerY + crossLength / 2);
    context.stroke();
  }, [
    currentTargets,
    backgroundColor,
    cellWidth,
    cellHeight,
    calculateCanvasDimensions,
    quadrillageVisible,
  ]);

  const isPositionInsideCanvas = (
    x: number,
    y: number,
    size: number, // Ajout de la taille
    canvasWidth: number,
    canvasHeight: number
  ) => {
    return (
      x >= 0 &&
      x + size <= canvasWidth && // S'assurer que la taille ne dépasse pas à droite
      y >= 0 &&
      y + size <= canvasHeight // S'assurer que la taille ne dépasse pas en bas
    );
  };

  const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

  useEffect(() => {
    showNewTarget();
  }, []);

  useEffect(() => {
    if (enableBlinkingAndResizing) {
      const animationInterval = setInterval(() => {
        setAnimationState((prevState) => ({
          scale: prevState.scale === 1 ? 1.15 : 1,
          opacity: prevState.opacity === 1 ? 0.45 : 1,
        }));
      }, 650);

      return () => clearInterval(animationInterval);
    }
  }, [enableBlinkingAndResizing]);

  const filterValidTargetsWithinSalvo = (
    salvo: Salvo,
    canvasWidth: number,
    canvasHeight: number,
    columnsToRemove: number,
    rowsToRemove: number
  ) => {
    return salvo.targets.filter((target) => {
      const { x, y } = convertGridPositionToCoordinates(
        target.position,
        columnsToRemove,
        rowsToRemove,
        cellWidth,
        cellHeight
      );
      const sizeInPixels = target.size * caseSize;
      return isPositionInsideCanvas(
        x,
        y,
        sizeInPixels,
        canvasWidth,
        canvasHeight
      );
    });
  };

  const showNewTarget = () => {
    if (canShowTarget && !isPausedAndNotFullScreenRef.current) {
      setCanShowTarget(false);
      const delay = 500;
      setTimeout(() => {
        const { canvasWidth, canvasHeight, columnsToRemove, rowsToRemove } =
          calculateCanvasDimensions();
        // Filtrer les salves qui ont au moins une cible valide dans le canvas
        const validTargetPositions = targetPositions
          .map((salvo) => {
            // Filtrer les cibles valides dans chaque salve
            const validTargets = filterValidTargetsWithinSalvo(
              salvo,
              canvasWidth,
              canvasHeight,
              columnsToRemove,
              rowsToRemove
            );
            // Retourner une nouvelle salve contenant uniquement les cibles valides
            return validTargets.length > 0
              ? { ...salvo, targets: validTargets }
              : null;
          })
          .filter((salvo) => salvo !== null); // Éliminer les salves vides

        if (validTargetPositions.length === 0) {
          setCanShowTarget(true); // Si aucune salve valide, permettre de relancer le processus
          return;
        }

        const salvo =
          validTargetPositions[
            Math.floor(Math.random() * validTargetPositions.length)
          ];

        // Sélection directe d'une salve sans filtrage
        // const salvo =
        //   targetPositions[Math.floor(Math.random() * targetPositions.length)];

        const targetCount = salvo?.targets.length || 0;
        const shuffledTargets = salvo?.targets;

        const newTargets =
          shuffledTargets?.map((target) => ({
            coordinates: [
              convertGridPositionToCoordinates(
                target.position,
                columnsToRemove,
                rowsToRemove,
                cellWidth,
                cellHeight
              ),
            ],
            gif: target.gif,
            size: target.size,
          })) || [];

        if (enableBlinkingAndResizing) {
          const blinkAndResizeCount = Math.floor(targetCount * 0.4); // 40% of targets
          const blinkAndResizeIndices = Array.from(
            { length: targetCount },
            (_, i) => i
          )
            .sort(() => Math.random() - 0.5)
            .slice(0, blinkAndResizeCount);
          setBlinkAndResizeTargets(blinkAndResizeIndices);
        }
        console.log("New targets shown:", newTargets);
        setCurrentTargets(newTargets);
        setCanShowTarget(true);
      }, delay);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "black", // backgroundColor prop passed here
      }}
    >
      <div
        style={{
          position: "relative",
          width: canvasWidth,
          height: canvasHeight,
          // display: "flex",
          // justifyContent: "center",
          // alignItems: "center",
          // justifyContent: "center",
          // alignItems: "center",
          // backgroundColor: "blue"
        }}
      >
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          style={{ position: "absolute", top: 0, left: 0 }}
        />
        {currentTargets.map((target, index) => {
          const firstCoord = target.coordinates[0];
          const size = (caseSize * target.size) / 2;

          // Calculate the center of the cell
          const cellCenterX = firstCoord.x + cellWidth / 2;
          const cellCenterY = firstCoord.y + cellHeight / 2;

          // Adjust the position to center the image
          const x = cellCenterX - size / 2;
          const y = cellCenterY - size / 2;

          return (
            <img
              key={index}
              src={target.gif}
              style={{
                position: "absolute",
                top: y,
                left: x,
                width: size,
                height: size,
                opacity:
                  enableBlinkingAndResizing &&
                  blinkAndResizeTargets.includes(index)
                    ? animationState.opacity
                    : 1,
                transform:
                  enableBlinkingAndResizing &&
                  blinkAndResizeTargets.includes(index)
                    ? `scale(${animationState.scale})`
                    : "scale(1)",
                transformOrigin: "center",
                transition: "opacity 0.3s ease, transform 0.3s ease",
              }}
              alt="target"
            />
          );
        })}

        <button
          onClick={showNewTarget}
          disabled={!canShowTarget || isPausedAndNotFullScreenRef.current}
        >
          Show Next Target
        </button>
        <div>{isPausedAndNotFullScreenRef.current ? "Paused" : ""}</div>
      </div>
    </div>
  );
};

export default ChampsProfondIdentification;
